import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import SearchComponent from '@components/inputs/search/Search.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';
import { Colors } from '@constants/colors.constants';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { GridValueGetterParams, GridValueSetterParams } from '@mui/x-data-grid-pro';
import { emitOnce } from '@ngneat/elf';
import { useObservable } from '@ngneat/react-rxjs';
import { auditDetailsQuery } from '@store/audits/audit-details/audits-details.query';
import { AuditOffer, CreateAuditOfferFollowUp, ProposalOffer } from '@store/audits/audit-offer-followUp/audit-offer-followUp.model';
import { auditsFollowUpService } from '@store/audits/audit-offer-followUp/audit-offer-followUp.services';
import { auditProposalSuppliersServices } from '@store/audits/audit-offer-followUp/audit-proposal-suppliers/audit-proposal-suppliers.services';
import { auditsOfferService } from '@store/audits/audit-offer/audit-offer.services';
import { auditsofferQuery } from '@store/audits/audit-offer/audits-offer.query';
import { AuditOperation, AuditOperationStepTypeEnum } from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationQuery } from '@store/audits/audits-operation/audits-operation.query';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { SuppliersUnit, suppliersUnitsQuery, suppliersUnitsService } from '@store/suppliers/units';
import { OfferFollowUpDetailsRoutes } from '@utils/routes.utils';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { FieldValidationType, checkIfErrors, getFieldError } from '@utils/yup.utils';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { finalize, switchMap } from 'rxjs';

interface NewProposalProps {
  handleClose: () => void;
  handleSetTab: () => void;
}

const NewProposal = (props: NewProposalProps) => {
  const { handleClose, handleSetTab } = props;
  const { auditId } = useParams();
  const { t } = useTranslation();
  const [version, setVersion] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [newProposalLoading, setNewProposalLoading] = useState(false);
  const [selectedProposalRow, setSelectedProposalRow] = useState<number[]>([]);
  const [selectedSupplierRow, setSelectedSupplierRow] = useState<number[]>([]);
  const [expiresAt, setExpiresAt] = useState<Date>();
  const [proposalName, setProposalName] = useState<string>('');
  const [allOffers, setAllOffers] = useState<ProposalOffer[]>([]);
  const navigate = useNavigate();


  const { enqueueSnackbar } = useSnackbar();

  const [{ auditDetails }] = useObservable(auditDetailsQuery.auditDetails$);
  const [{ auditsInfoOP }] = useObservable(auditsOperationQuery.auditsOP$);
  const [{ auditsOffer }] = useObservable(auditsofferQuery.auditsofferList$);
  const [{ suppliersUnits }] = useObservable(suppliersUnitsQuery.suppliersUnits$);

  const [filters] = useObservable(suppliersUnitsQuery.filters$);

  const getAuditOpbyId = (OperationId: string | number) =>
    auditsInfoOP.find(op => op.id === OperationId) || [];

  const getAllOffers = () => auditsOffer.flatMap(offer => {
    const auditInfOp = getAuditOpbyId(offer.auditOperationId) as AuditOperation;
    if (!auditInfOp) return [];
    return offer.auditOffer.map(it => ({ ...it, container: auditInfOp.container, waste: auditInfOp.waste })) as unknown as ProposalOffer;
  });

  useEffect(() => {
    emitOnce(() => {
      auditsOperationService.setStep(AuditOperationStepTypeEnum.PRECO);
    });
    if (auditDetails.id) {
      setLoading(true);
      setSupplierLoading(true);
      suppliersUnitsService.getSuppliersUnits().pipe(
        finalize(() => setSupplierLoading(false))
      ).subscribe();
      auditsOfferService.getAuditOffer(auditDetails.id, version ?? auditDetails.currentVersion).pipe(
        switchMap(() => auditsOperationService.getAuditsInformationOP(auditDetails.id)),
        finalize(() => setLoading(false))
      ).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }

    return () => {
      auditsOfferService.resetStore();
    };
  }, [auditDetails, version, enqueueSnackbar]);

  useEffect(() => {
    if (!loading) {
      const selectedOffers = getAllOffers().filter((it) => it.id);
      setSelectedProposalRow(selectedOffers.map((it) => it.id));
      setAllOffers(getAllOffers());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (auditDetails.id) {
      setVersion(auditDetails.currentVersion);
    }
  }, [auditDetails]);

  useEffect(() => {
    suppliersUnitsService.getSuppliersUnits().subscribe();
  }, [filters]);

  const handleSelectedProposalRows = (params: any) => setSelectedProposalRow([...params]);
  const handleSelectedSupplierRows = (params: any) => setSelectedSupplierRow([...params]);
  const handleChange = (value: string) => setProposalName(value);

  const errors = {
    proposalName: getFieldError(proposalName, FieldValidationType.REQUIRED_STRING),
    expiresAt: getFieldError(expiresAt, FieldValidationType.REQUIRED_TIME),
  };

  const condition = checkIfErrors(errors) || !selectedProposalRow.length || !selectedSupplierRow.length || newProposalLoading;

  const handleValidate = () => {
    if (auditId && expiresAt) {
      setNewProposalLoading(true);
      const selectedOffers = allOffers.filter((it) => selectedProposalRow.includes(it.id));
      const selectedSuppliers = suppliersUnits.filter((it) => selectedSupplierRow.includes(it.id));
      const payload: CreateAuditOfferFollowUp = {
        auditOffers: selectedOffers.map((offer) => {
          return { auditOfferId: offer.id, initialCostInCents: offer.unitPriceInCents } as unknown as AuditOffer;
        }),
        supplierOrganisationUnitIds: selectedSuppliers.map((supplier) => supplier.id),
        expiresAt: expiresAt?.toISOString(),
        proposalName: proposalName,

      };
      auditsFollowUpService.createAuditProposal(auditId, payload).pipe(
        switchMap(() => auditProposalSuppliersServices.getAllAuditsSupProposal(auditId)),
        switchMap(() => auditsFollowUpService.getAuditOfferFollowUp(auditId, false)),
        finalize(() => {
          setNewProposalLoading(false);
          handleClose();
          handleSetTab();
        })
      ).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
        next: (value) => {
          const valueSorted = value.data.sort((a, b) => a.createdAt > b.createdAt ? 1 : -1);
          enqueueSnackbar(t('audits.auditOfferFollowUp.success.createOffer'), { variant: 'success' });
          navigate(`${OfferFollowUpDetailsRoutes.DETAILS_OFFER}/${valueSorted[value.count - 1].id}`);
        },
      });
    }
  };

  return (
    <ModalComponent
      title={t('audits.auditOfferFollowUp.modal.title')}
      fullScreen
      handleClose={handleClose}
      content={(
        <Stack flex={1} width={'100%'} justifyContent="start" direction="row" spacing={4} px="30px" pt='25px' bgcolor="#F4F6F8" >
          <Stack width={'100%'} maxWidth="440px" spacing={2}>
            <Typography>
              Partenaire
            </Typography>
            <SearchComponent
              isCustom
              placeholder={t('audits.auditOfferFollowUp.searchPartnersPlaceholder')}
              handleChange={(value) => suppliersUnitsService.setFilters('search')(value)}
            />
            <Stack flex={1} width={'100%'} bgcolor={Colors.white}>
              <NewTableComponent
                columns={[
                  COLUMN_TYPES.TEXT({
                    field: 'name',
                    color: 'secondary',
                    label: 'Partenaire',
                    width: 300,
                    valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.name,
                    renderCell: (params) => (
                      <Stack direction="row" spacing={2}>
                        <Typography maxWidth={300} sx={{ whiteSpace: 'wrap' }}>
                          {params.row.name}
                        </Typography>
                      </Stack>
                    ),
                  }),
                ]}
                handleRowSelected={handleSelectedSupplierRows}
                rowsSelected={selectedSupplierRow}
                disablePagination
                loading={supplierLoading}
                data={suppliersUnits}
              />
            </Stack>
          </Stack>
          <Stack width="100%" spacing={2} >
            <Typography>
              Contenants et prestations
            </Typography>
            <Stack height="40px">
              {/* Filters */}
            </Stack>
            <Stack flex={1} width={'100%'} bgcolor={Colors.white}>
              <NewTableComponent
                columns={[
                  COLUMN_TYPES.TEXT({
                    field: 'container.name',
                    width: 200,
                    color: 'secondary',
                    label: 'Contenants',
                    valueGetter: (params: GridValueGetterParams<any, ProposalOffer>) => params.row.container?.name,
                    valueFormatter: (params) => params.value,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'waste.name',
                    width: 200,
                    color: 'secondary',
                    label: 'Type de déchet',
                    valueGetter: (params: GridValueGetterParams<any, ProposalOffer>) => params.row.waste?.name,
                    valueFormatter: (params) => params.value,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'Prestation',
                    width: 200,
                    color: 'secondary',
                    label: 'Prestation',
                    valueGetter: (params: GridValueGetterParams<any, ProposalOffer>) => params.row.type,
                    valueFormatter: (params) => params.value,
                  }),
                  COLUMN_TYPES.NUMBER({
                    field: 'unitPriceInCents',
                    label: 'Prix',
                    sortable: true,
                    valueGetter: (params: GridValueGetterParams<any, ProposalOffer>) => params.row.unitPriceInCents,
                    valueFormatter: (params) => params.value ? (params?.value / 100).toFixed(2) : '-',
                    valueSetter: (params: GridValueSetterParams) => {
                      return {
                        ...params.row,
                        unitPriceInCents: params.row.unitPriceInCents === params.value ? params.value : params.value * 100,
                      };
                    },
                    unitName: () => (
                      <Typography fontSize='14px' fontWeight={400} color='#868F9C'>
                        CHF
                      </Typography>
                    ),
                    width: 150,
                  }),
                ]}
                disablePagination
                handleRowSelected={handleSelectedProposalRows}
                rowsSelected={selectedProposalRow}
                editableFields={['unitPriceInCents']}
                disableRowSelectionOnClick={true}
                loading={loading}
                handleRowChanged={(value: ProposalOffer) => {
                  const allOffersCopy = [...allOffers];
                  const index = allOffersCopy.findIndex((it) => it.id === value.id);
                  allOffersCopy[index].unitPriceInCents = value.unitPriceInCents ?? 0;
                  setAllOffers(allOffersCopy);
                  return { ...allOffersCopy, ...value };
                }}
                data={allOffers}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
      actions={(
        <>
          <Stack direction="row" spacing={2}>
            <DatePickerComponent
              label={t('audits.auditOfferFollowUp.modal.limitDate')}
              placeholder={t('global.selectDate')}
              value={expiresAt}
              inputWidth={175}
              disablePast
              handleChange={(value) => setExpiresAt(value as Date)}
              fontStyle={{ fontSize: '18px', color: 'black', fontWeight: 500 }}
              required
              inputStyle={{
                backgroundColor: 'white',
                borderRadius: '0px',
                ':hover': {
                  backgroundColor: 'white !important',
                },
              }}
              error={errors.expiresAt}
            />
            <TextFieldComponent
              color="secondary"
              label={t('settings.waste.manage.subCategory.name')}
              placeholder={t('global.writeSomething')}
              handleChange={handleChange}
              fontStyle={{ fontSize: '18px', color: 'black', fontWeight: 500 }}
              inputStyle={{
                backgroundColor: 'white',
                borderRadius: '0px',
                ':hover': {
                  backgroundColor: 'white !important',
                },
              }}
              value={proposalName}
              error={errors.proposalName}
              required />
            <Button
              variant="contained"
              disabled={condition}
              onClick={handleValidate}
              color="primary">
              {newProposalLoading ? <CircularProgress sx={{ color: Colors.white }} size={15} thickness={10} /> : t('global.validate')}
            </Button>
          </Stack>
        </>
      )} />
  );
};

export default NewProposal;